import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { GridPaginationModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { retryConfigPageSizeOptions } from 'src/constants/scheduler/constants'
import { buildScheduledActionPath } from 'src/constants/routes'
import type { RetryConfigListRow, RetryConfigSortDirection } from 'src/interfaces/retryConfig'
import {
  getRetryConfigs,
  getRetryConfigsLoading,
  getRetryConfigsPagination,
} from 'src/selectors/retryConfig'
import { EmptyScheduledActions } from './EmptyScheduledActions'
import { scheduledActionColumns } from './scheduledActionColumns'

interface Props {
  hasPartner: boolean
  paginationModel: GridPaginationModel
  sortDirection: RetryConfigSortDirection
  onPaginationModelChange(model: GridPaginationModel): void
  onSortDirectionChange(direction: RetryConfigSortDirection): void
}

const getRowId = (row: RetryConfigListRow): string => {
  return row.id
}

const ScheduledActionsTableComponent = ({
  hasPartner,
  paginationModel,
  sortDirection,
  onPaginationModelChange,
  onSortDirectionChange,
}: Props) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const configs = useSelector(getRetryConfigs)
  const pagination = useSelector(getRetryConfigsPagination)
  const isLoading = useSelector(getRetryConfigsLoading)

  // The current filters ride along so Back from the detail page restores the same view.
  const handleRowClick = useCallback(
    (params: GridRowParams<RetryConfigListRow>) => {
      navigate({
        pathname: buildScheduledActionPath(params.row.id),
        search: searchParams.toString(),
      })
    },
    [navigate, searchParams],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const scheduledAtSort = model.find((item) => {
        return item.field === 'scheduledAt'
      })

      onSortDirectionChange(scheduledAtSort?.sort === 'desc' ? 'DESC' : 'ASC')
    },
    [onSortDirectionChange],
  )

  const renderEmpty = useCallback(() => {
    return <EmptyScheduledActions hasPartner={hasPartner} />
  }, [hasPartner])

  // Server pagination. `rowCount` is required: without it the footer computes the page count
  // from `rows.length` and the grid never offers page 2.
  //
  // Rows and count are gated on `hasPartner` because the slice cannot clear them itself: with no
  // partner the tab never dispatches a fetch (the endpoint's `partnerId` is `@IsNotEmpty`), so
  // the reducer never sees a REQUEST and its partner-change guard never runs. The previous
  // partner's rows would otherwise stay visible and clickable underneath the "Select a partner"
  // empty state. Reachable by pressing Back to a URL that predates the partner selection — the
  // dropdown itself has no clear option. A switch between two real partners is already handled
  // by the reducer, which clears `configs` and `pagination` on that REQUEST.
  return (
    <GridTable<RetryConfigListRow>
      rows={hasPartner ? configs : []}
      columns={scheduledActionColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      paginationMode="server"
      rowCount={hasPartner ? pagination.total ?? 0 : 0}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={retryConfigPageSizeOptions}
      sortingMode="server"
      sortModel={[{ field: 'scheduledAt', sort: sortDirection === 'DESC' ? 'desc' : 'asc' }]}
      onSortModelChange={handleSortModelChange}
      // Rows open the detail page, so they read as clickable — the grid's default cursor is
      // the text caret.
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      EmptyElement={renderEmpty}
      tableDataTestId="scheduled-actions-table"
      rowDataTestIdPrefix="scheduled-actions"
    />
  )
}

export const ScheduledActionsTable = memo(ScheduledActionsTableComponent)
